import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { format, startOfMonth, endOfMonth, eachDayOfInterval, startOfWeek, endOfWeek, isSameMonth, isSameDay, parseISO, isAfter, isToday, addDays } from 'date-fns';
import { motion, AnimatePresence } from 'framer-motion';
import { FaClock, FaMapMarkerAlt, FaUserFriends } from 'react-icons/fa';
import { ChevronLeftIcon, ChevronRightIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { setSelectedDate, setSharedEvents, setLoadingShared, setSharedEventsError, updateEvent, deleteEvent } from '../state';
import { fetchShareRequests } from '../../../state/calendarSlice';
import EventDetailsDialog from './EventDetailsDialog';
import calendarService from '../../../services/calendarService';
import ShareEventDialog from './ShareEventDialog';

export default function MonthView({ showSharedEvents = true }) {
  const dispatch = useDispatch();
  const selectedDateString = useSelector((state) => state.calendar.selectedDate);
  const selectedDate = useMemo(() => new Date(selectedDateString), [selectedDateString]);
  const user = useSelector((state) => state.auth.user);
  const token = useSelector((state) => state.auth.token);
  const [events, setEvents] = useState([]);
  const sharedEvents = useSelector((state) => state.calendar.sharedEvents || []);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [feedback, setFeedback] = useState('');
  const [isShareDialogOpen, setIsShareDialogOpen] = useState(false);
  const [eventToShare, setEventToShare] = useState(null);
  const isLoadingShared = useSelector((state) => state.calendar.isLoadingShared);
  const sharedEventsError = useSelector((state) => state.calendar.sharedEventsError);
  const pendingShareRequests = useSelector((state) => 
    state.calendar.shareRequests?.filter(req => 
      req.status === 'pending' && req.recipientId === user?.id
    ) || []
  );
  
  // Track fetch status with useRef
  const fetchStatusRef = useRef({
    month: null,
    userId: null,
    sharedFetched: false
  });

  // Memoize date-related values
  const monthStart = useMemo(() => startOfMonth(selectedDate), [selectedDate]);
  const monthEnd = useMemo(() => endOfMonth(selectedDate), [selectedDate]);
  const calendarStart = useMemo(() => startOfWeek(monthStart, { weekStartsOn: 1 }), [monthStart]);
  const calendarEnd = useMemo(() => endOfWeek(monthEnd, { weekStartsOn: 1 }), [monthEnd]);
  const daysInMonth = useMemo(() => 
    eachDayOfInterval({ start: calendarStart, end: calendarEnd }), 
    [calendarStart, calendarEnd]
  );
  
  // Weekday headers
  const weekDays = useMemo(() => [
    { key: 'mon', label: 'Mon' },
    { key: 'tue', label: 'Tue' },
    { key: 'wed', label: 'Wed' },
    { key: 'thu', label: 'Thu' },
    { key: 'fri', label: 'Fri' },
    { key: 'sat', label: 'Sat' },
    { key: 'sun', label: 'Sun' }
  ], []);

  const fetchEvents = useCallback(async () => {
    if (!user?.id) return;
    
    const monthStr = format(selectedDate, 'yyyy-MM');
    
    // Skip if we've already fetched for this month and user
    if (fetchStatusRef.current.month === monthStr && 
        fetchStatusRef.current.userId === user.id) {
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const response = await calendarService.fetchMonthEvents(monthStr, user.id);
      const eventsArray = response.events || [];
      
      setEvents(eventsArray);
      
      // Update fetch status
      fetchStatusRef.current.month = monthStr;
      fetchStatusRef.current.userId = user.id;
    } catch (error) {
      setError('Failed to load events. Please try again.');
      setEvents([]);
    } finally {
      setLoading(false);
    }
  }, [user?.id, selectedDate]);

  const fetchSharedEvents = useCallback(async () => {
    if (!user?.id) return;
    
    // Skip if we've already fetched shared events for this user
    if (fetchStatusRef.current.sharedFetched && 
        fetchStatusRef.current.userId === user.id) {
      return;
    }
    
    try {
      dispatch(setLoadingShared(true));
      
      const response = await calendarService.fetchSharedEvents(user.id);
      const sharedEventsData = response.events || [];
      
      dispatch(setSharedEvents(sharedEventsData));
      
      // Update fetch status
      fetchStatusRef.current.sharedFetched = true;
      fetchStatusRef.current.userId = user.id;
    } catch (error) {
      dispatch(setSharedEventsError('Failed to load shared events'));
    } finally {
      dispatch(setLoadingShared(false));
    }
  }, [user?.id, dispatch]);



  // Reset fetch status when month changes
  useEffect(() => {
    const currentMonth = format(selectedDate, 'yyyy-MM');
    if (fetchStatusRef.current.month !== currentMonth) {
      fetchStatusRef.current.month = null;
    }
  }, [selectedDate]);

  // Reset shared events fetch status when user changes
  useEffect(() => {
    if (user?.id !== fetchStatusRef.current.userId) {
      fetchStatusRef.current.sharedFetched = false;
    }
  }, [user?.id]);

  // Fetch events when month changes or user changes
  useEffect(() => {
    if (user?.id) {
      fetchEvents();
    }
  }, [fetchEvents]);

  // Fetch shared events only when user changes
  useEffect(() => {
    if (user?.id) {
      fetchSharedEvents();
    }
  }, [fetchSharedEvents]);

  // Fetch share requests when component mounts or user changes
  // useEffect(() => {
  //   if (user?.id) {
  //     fetchShareRequestsData();
  //   }
  // }, [fetchShareRequestsData]);

  // Combine events and shared events
  const allEvents = useMemo(() => {
    if (!user?.id) return [];
    
    // Get pending event IDs
    const pendingEventIds = pendingShareRequests.map(req => req.eventId);
    
    // First determine which events are owned by the current user vs shared with them
    const ownedEvents = [];
    const receivedSharedEvents = [];
    
    // Process events from regular events array
    events.forEach(event => {
      // Check if this event is owned by current user or shared with them
      if (event.userId === user.id) {
        // This is user's own event
        ownedEvents.push({...event});
      } else {
        // This event belongs to someone else but shared with current user
        // Only add if showSharedEvents is true and it's not a pending request
        if (showSharedEvents && !pendingEventIds.includes(event.id)) {
          receivedSharedEvents.push({...event, isShared: true});
        }
      }
    });
    
    // Process events from shared events array if toggle is on
    if (showSharedEvents) {
      sharedEvents.forEach(event => {
        // Only add if it's not user's own event, not already added, and not pending
        if (event.userId !== user.id && 
            !pendingEventIds.includes(event.id) && 
            !ownedEvents.some(e => e.id === event.id) && 
            !receivedSharedEvents.some(e => e.id === event.id)) {
          receivedSharedEvents.push({...event, isShared: true});
        }
      });
    }
    
    // Combine the lists - owned events are always shown, shared events only when toggle is on
    return [...ownedEvents, ...(showSharedEvents ? receivedSharedEvents : [])];
  }, [events, sharedEvents, showSharedEvents, pendingShareRequests, user]);

  // Filter events for a specific day
  const getEventsForDay = useCallback((day) => {
    return allEvents.filter(event => {
      try {
        const startTime = event.startTime || event.start;
        if (!startTime) return false;
        
        return isSameDay(parseISO(startTime), day);
      } catch (error) {
        return false;
      }
    });
  }, [allEvents]);

  // Event handlers
  const handleDeleteEvent = async (event) => {
    try {
      await calendarService.deleteEvent(event.id);
      
      // Remove event from local state
      setEvents(prev => prev.filter(e => e.id !== event.id));
      setSelectedEvent(null);
      
      setFeedback('Event deleted successfully');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      setFeedback('Failed to delete event');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const handleEditEvent = async (updatedEvent) => {
    try {
      const result = await calendarService.updateEvent(updatedEvent);
      
      // Update event in local state
      setEvents(prev => prev.map(e => e.id === updatedEvent.id ? updatedEvent : e));
      
      setFeedback('Event updated successfully');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      setFeedback('Failed to update event');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const handleShareEvent = async (event) => {
    if (!event?.id) return;
    
    try {
      // Open the share dialog
      setEventToShare(event);
      setIsShareDialogOpen(true);
    } catch (error) {
      setFeedback('Failed to share event. Please try again.');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  const handleSetReminder = async (event) => {
    try {
      const reminderTime = prompt('Set reminder time before event (in minutes):', '30');
      if (!reminderTime) return;
      
      await calendarService.setReminder(event.id, {
        userId: user.id,
        reminderTime: parseInt(reminderTime)
      });
      
      setFeedback('Reminder set successfully');
      setTimeout(() => setFeedback(''), 3000);
    } catch (error) {
      setFeedback('Failed to set reminder');
      setTimeout(() => setFeedback(''), 3000);
    }
  };

  // Render day cell with events
  const renderDayCell = (day, index) => {
    const dayEvents = getEventsForDay(day);
    const isSelectedMonth = isSameMonth(day, selectedDate);
    const isCurrentDay = isToday(day);
    const isSelected = isSameDay(day, selectedDate);
    
    return (
      <motion.div
        key={day.toString()}
        initial={{ opacity: 0, scale: 0.98 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3, delay: index * 0.01 }}
        className={`min-h-[120px] p-3 border-b border-r border-pri-white/10 relative
          ${!isSelectedMonth ? 'bg-pri-dark/30 opacity-70' : ''}
          ${isCurrentDay ? 'bg-gradient-to-br from-pri-yellow/5 to-pri-green/5' : ''}
          ${isSelected ? 'bg-gradient-to-br from-pri-yellow/10 to-pri-green/10' : ''}
          hover:bg-gradient-to-br hover:from-pri-yellow/10 hover:to-pri-green/10 transition-all duration-300
          cursor-pointer group`}
        onClick={() => {
          dispatch(setSelectedDate({ selectedDate: day.toString() }));
          setSelectedDay(day);
        }}
      >
        {/* Date Label */}
        <div className={`text-right mb-2 ${
          isCurrentDay 
            ? 'text-pri-yellow font-bold'
            : !isSelectedMonth
            ? 'text-pri-white/30'
            : 'text-pri-white'
        }`}>
          <span 
            className={`text-sm inline-flex items-center justify-center ${
              isCurrentDay || isSelected 
                ? 'bg-gradient-to-br from-pri-yellow/20 to-pri-green/20 h-6 w-6 rounded-full'
                : ''
            }`}
          >
            {format(day, 'd')}
          </span>
        </div>

        {/* Events List */}
        <div className="space-y-1">
          {dayEvents.slice(0, 3).map((event) => (
            <motion.div
              key={event.id}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.95 }}
              className={`p-2 rounded-lg text-xs shadow-lg backdrop-blur-sm transition-all duration-300 relative overflow-hidden ${
                event.isShared ? 'border border-pri-yellow/50' : ''
              }`}
              style={{
                background: event.color || 'linear-gradient(to right, var(--soft-pink), var(--mint-green))',
                backgroundImage: event.isShared 
                  ? 'linear-gradient(45deg, rgba(255,255,255,0.1) 25%, transparent 25%, transparent 50%, rgba(255,255,255,0.1) 50%, rgba(255,255,255,0.1) 75%, transparent 75%, transparent), ' + (event.color ? 'none' : 'linear-gradient(to right, var(--soft-pink), var(--mint-green))')
                  : 'none',
                backgroundSize: event.isShared ? '8px 8px, 100%' : '100%'
              }}
              onClick={(e) => {
                e.stopPropagation();
                setSelectedEvent(event);
              }}
            >
              {/* Glossy effects */}
              <div className="absolute inset-0 pointer-events-none">
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-white/5 to-transparent" />
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-white/30 to-transparent" />
              </div>
              
              <div className="relative">
                <div className="flex items-center space-x-1">
                  <FaClock className="h-3 w-3 text-pri-dark/80 flex-shrink-0" />
                  <span className="text-pri-dark font-medium truncate">
                    {format(parseISO(event.startTime || event.start), 'h:mm a')}
                  </span>
                  {event.isShared && <FaUserFriends className="ml-1 h-2.5 w-2.5 text-pri-dark/80 flex-shrink-0" />}
                </div>
                <div className="font-medium text-pri-dark truncate mt-0.5">
                  {event.title}
                </div>
              </div>
            </motion.div>
          ))}

          {/* More Events Indicator */}
          {dayEvents.length > 3 && (
            <div 
              className="text-right mt-1 opacity-70 group-hover:opacity-100 transition-opacity"
              onClick={(e) => {
                e.stopPropagation();
                dispatch(setSelectedDate({ selectedDate: day.toString() }));
              }}
            >
              <motion.span 
                whileHover={{ scale: 1.05 }}
                className="text-xs inline-block px-2 py-0.5 rounded-full bg-gradient-to-r from-soft-pink/20 to-mint-green/20 text-pri-dark cursor-pointer border border-soft-pink/10 backdrop-blur-sm shadow-sm"
              >
                +{dayEvents.length - 3} more
              </motion.span>
            </div>
          )}
        </div>
      </motion.div>
    );
  };

  return (
    <div className="flex flex-col h-full bg-gradient-to-br from-pri-dark via-[#1a1c2e] to-sec-dark text-pri-white">
      <div className="flex-1 flex flex-col overflow-hidden">
        {/* Month Header and Days of Week */}
        <div className="sticky top-0 z-10 bg-gradient-to-b from-pri-dark/95 to-pri-dark/80 backdrop-blur-xl border-b border-pri-white/10">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 border-b border-pri-white/10">
            {weekDays.map((day) => (
              <div key={day.key} className="px-2 py-3 text-center">
                <span className="text-sm font-medium text-pri-yellow">{day.label}</span>
              </div>
            ))}
          </div>
        </div>
        
        {/* Month Grid */}
        <div className="flex-1 overflow-auto">
          <div className="grid grid-cols-7 bg-gradient-to-b from-pri-dark/80 to-pri-dark/90">
            {/* Calendar Days */}
            {daysInMonth.map((day, index) => renderDayCell(day, index))}
          </div>
        </div>
      </div>
      
      {/* Event Details Dialog */}
      <AnimatePresence>
        {selectedEvent && (
          <EventDetailsDialog
            event={selectedEvent}
            onClose={() => setSelectedEvent(null)}
            onDelete={() => handleDeleteEvent(selectedEvent)}
            onEdit={handleEditEvent}
            onShare={() => handleShareEvent(selectedEvent)}
            onSetReminder={() => handleSetReminder(selectedEvent)}
            isShared={selectedEvent.isShared}
          />
        )}
      </AnimatePresence>
      
      {/* Loading Indicator */}
      <AnimatePresence>
        {loading && (
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed top-4 right-4 bg-gradient-to-r from-pri-yellow to-pri-green px-3 py-1.5 rounded-lg text-pri-dark text-sm font-medium shadow-lg"
          >
            Loading...
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Feedback Toast */}
      <AnimatePresence>
        {feedback && (
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 50 }}
            className="fixed bottom-4 left-1/2 transform -translate-x-1/2 bg-pri-yellow text-pri-dark px-4 py-2 rounded-xl shadow-lg"
          >
            {feedback}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Share Event Dialog */}
      <ShareEventDialog
        isOpen={isShareDialogOpen}
        onClose={() => setIsShareDialogOpen(false)}
        event={eventToShare}
        onEventShared={() => {
          setFeedback('Event shared successfully!');
          setTimeout(() => setFeedback(''), 3000);
          
          // Refresh events after sharing
          fetchEvents();
        }}
      />
    </div>
  );
}
